import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  getEmailSubscriptions,
  deleteEmailSubscription,
} from "../../services/emailSubscriptionsService";
import { paginateData, calculateTotalPages } from "../../assets/js/script";
import TableHeader from "../Common/TableComponent/TableHeader";
import TableToolbar from "../Common/TableComponent/TableToolbar";
import TablesRow from "../Common/TableComponent/TablesRow";
import { Pagination } from "../Common/TableComponent/Pagination";
import { Loading } from "../Common/OtherElements/Loading";
import { handleErrors } from "../../utils/errorHandler";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";
import ComponentHeader from "../Common/OtherElements/ComponentHeader";

const SUBSCRIBERS_COLUMNS = [
  "SL No",
  "Email ID",
  "Subscribed On",
  "Status",
  "Actions",
];

const getSubscriberId = (item) => item.id ?? item.Id;

const getSubscriberEmail = (item) =>
  item.email ?? item.Email ?? item.emailId ?? item.EmailId ?? "-";

const getSubscriberAddedOn = (item) =>
  item.addedOn ??
  item.AddedOn ??
  item.subscribedOn ??
  item.SubscribedOn ??
  item.createdOn ??
  item.CreatedOn ??
  "";

const isSubscriberActive = (item) => {
  const raw =
    item.status ??
    item.Status ??
    item.isActive ??
    item.IsActive ??
    item.isSubscribed ??
    item.IsSubscribed;
  if (raw === true || raw === 1) return true;
  const normalized = String(raw ?? "").trim().toLowerCase();
  return (
    normalized === "active" ||
    normalized === "1" ||
    normalized === "true" ||
    normalized === "subscribed"
  );
};

const formatSubscribedOn = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const datePart = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} ${timePart}`;
};

const renderStatusBadge = (active) => (
  <span className={`badge badge-soft-${active ? "success" : "danger"}`}>
    {active ? "Active" : "Inactive"}
  </span>
);

export const ManageSubscribers = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "manage-subscribers";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [subscribersList, setSubscribersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getEmailSubscriptions();
      const list = response.data?.result || response.data || [];
      setSubscribersList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = subscribersList.filter((item) => {
    const email = getSubscriberEmail(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return email.includes(query);
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (subscriberId) => {
    const result = await Swal.fire({
      title: "Delete Subscriber?",
      text: "Are you sure you want to delete this subscriber?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await deleteEmailSubscription(subscriberId);
        Swal.fire(
          "Deleted!",
          "The subscriber has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  const tableAccess = {
    ...pageAccessDetails,
    addAccess: false,
    editAccess: false,
  };

  return (
    <>
      <ComponentHeader title="Manage Subscribers" />

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h5 className="mb-0">Manage Subscribers</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader columns={SUBSCRIBERS_COLUMNS} />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <tr>
                            <td
                              colSpan={SUBSCRIBERS_COLUMNS.length}
                              className="text-center"
                            >
                              <i className="ri-error-warning-line me-3 align-middle fs-16 text-danger"></i>
                              <strong>No subscribers found.</strong>
                            </td>
                          </tr>
                        ) : (
                          currentData.map((item, index) => (
                            <TablesRow
                              key={getSubscriberId(item)}
                              rowData={{
                                slNo:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                email: getSubscriberEmail(item),
                                subscribedOn: formatSubscribedOn(
                                  getSubscriberAddedOn(item)
                                ),
                                status: renderStatusBadge(
                                  isSubscriberActive(item)
                                ),
                              }}
                              columns={[
                                "slNo",
                                "email",
                                "subscribedOn",
                                "status",
                              ]}
                              hideIcons={false}
                              onEdit={() => {}}
                              onDelete={() =>
                                handleDelete(getSubscriberId(item))
                              }
                              pageLevelAccessData={tableAccess}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
